/**
 * @file inward-assign-modal.tsx
 * @module app/modules/inward/components/inward-assign-modal
 * @description Assign or change the loading bay manager and dock of a PO. A change applies
 *   to the next GRN; GRNs already saved keep who did them and at which dock.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import { error, success } from "../../../../Global/toast";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { eatToday } from "../utils/inward-po";
import { InwardOption } from "./inward-filter-bar";

export interface InwardAssignmentInfo {
  loadingBayManager?: { reference?: string; name?: string };
  dock?: { reference?: string; name?: string };
  gateEntryNumber?: string;
  scheduledDate?: string;
  assignedBy?: { name?: string };
  assignedAt?: string;
}

interface InwardAssignModalProps {
  poId: string;
  poRefKey: string;
  current?: InwardAssignmentInfo | null;
  onClose: () => void;
  onSaved: () => void;
}

const InwardAssignModal = ({
  poId,
  poRefKey,
  current,
  onClose,
  onSaved,
}: InwardAssignModalProps) => {
  const { selectedWarehouse } = useWarehouse();
  const [managers, setManagers] = useState<InwardOption[]>([]);
  const [docks, setDocks] = useState<InwardOption[]>([]);
  const [manager, setManager] = useState(
    current?.loadingBayManager?.reference ?? ""
  );
  const [dock, setDock] = useState(current?.dock?.reference ?? "");
  const [gateEntryNumber, setGateEntryNumber] = useState(
    current?.gateEntryNumber ?? ""
  );
  const [scheduledDate, setScheduledDate] = useState(
    current?.scheduledDate ?? eatToday()
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const response = await new APICallService(
        INWARD.assignmentOptions,
        "",
        { id: poId, action: "assignment-options" },
        "",
        "",
        "",
        PermissionModule.Inward,
        selectedWarehouse?._id
      ).callAPI();
      setManagers(response?.loadingBayManagers ?? []);
      setDocks(response?.docks ?? []);
    })();
  }, [poId, selectedWarehouse?._id]);

  const save = async () => {
    if (!manager || !dock) {
      error("Select a loading bay manager and a dock");
      return;
    }
    if (!gateEntryNumber.trim()) {
      error("Enter the gate entry number");
      return;
    }
    if (!scheduledDate) {
      error("Select the inward date");
      return;
    }
    setSaving(true);
    const response = await new APICallService(
      INWARD.assignLoadingBay,
      {
        loadingBayManager: manager,
        dock,
        gateEntryNumber: gateEntryNumber.trim(),
        scheduledDate,
      },
      { id: poId, action: "assignment" },
      "",
      "",
      "",
      PermissionModule.Inward,
      selectedWarehouse?._id
    ).callAPI();
    setSaving(false);
    if (response) {
      success(`Inward planned for ${poRefKey} on ${scheduledDate}`);
      onSaved();
    }
  };

  return (
    <Modal show centered onHide={onClose} data-testid="assign-modal">
      <Modal.Header closeButton>
        <Modal.Title className="fs-20">
          {current ? "Change" : "Plan"} inward · {poRefKey}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-4">
        {current && (
          <div className="fs-14 text-muted" data-testid="assign-current">
            Now: {current.loadingBayManager?.name || "-"} ·{" "}
            {current.dock?.name || "-"} · {current.scheduledDate || "-"}. A
            change applies to the next GRN.
          </div>
        )}
        <Form.Group controlId="assign-manager">
          <Form.Label className="fs-15 fw-semibold">
            Loading bay manager
          </Form.Label>
          <Form.Select
            className="inward-input"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            data-testid="assign-manager"
          >
            <option value="">Select loading bay manager</option>
            {managers.map((m) => (
              <option key={m._id} value={m._id}>
                {m.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="assign-dock">
          <Form.Label className="fs-15 fw-semibold">Dock</Form.Label>
          <Form.Select
            className="inward-input"
            value={dock}
            onChange={(e) => setDock(e.target.value)}
            data-testid="assign-dock"
          >
            <option value="">Select dock</option>
            {docks.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="assign-gate-entry">
          <Form.Label className="fs-15 fw-semibold">
            Gate entry number
          </Form.Label>
          <Form.Control
            className="inward-input"
            value={gateEntryNumber}
            onChange={(e) => setGateEntryNumber(e.target.value)}
            data-testid="assign-gate-entry"
          />
        </Form.Group>
        <Form.Group controlId="assign-date">
          <Form.Label className="fs-15 fw-semibold">Inward date</Form.Label>
          <Form.Control
            className="inward-input"
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            data-testid="assign-date"
          />
          <Form.Text className="fs-13">
            The loading bay manager sees this PO on that day.
          </Form.Text>
        </Form.Group>
        {managers.length === 0 && (
          <div className="fs-14 text-danger" data-testid="assign-no-managers">
            No loading bay managers for this warehouse. In Master › Roles &
            Permissions, tick Inward › Loading Bay Manager on their role.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="inward-touch-btn" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="inward-touch-btn"
          disabled={saving}
          onClick={save}
          data-testid="assign-save"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InwardAssignModal;
